import { Link } from 'react-router-dom';
import { IconAlertCircle, IconChevronRight } from '@tabler/icons-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { relativeTime } from '@/lib/utils';
import { api } from '../api.js';

const PAGE_SIZE = 50;

export function ChangesPage() {
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['changes'],
      initialPageParam: undefined as number | undefined,
      queryFn: ({ pageParam }) =>
        api.changes({ max: pageParam, limit: PAGE_SIZE }),
      getNextPageParam: (lastPage) => {
        if (lastPage.changes.length < PAGE_SIZE) return undefined;
        const last = lastPage.changes[lastPage.changes.length - 1];
        return last?.change;
      },
    });

  const allChanges = data?.pages.flatMap((p) => p.changes) ?? [];

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight">Changelists</h1>
        {data && <Badge>{allChanges.length}</Badge>}
      </div>

      {isLoading && (
        <div className="flex items-center gap-2">
          <Spinner />
          <span className="text-sm text-muted-foreground">Loading…</span>
        </div>
      )}

      {error && (
        <Alert tone="destructive">
          <IconAlertCircle />
          <AlertTitle>Failed to load changes</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      {data && allChanges.length === 0 && (
        <p className="py-12 text-center text-sm text-muted-foreground">
          No changelists submitted yet.
        </p>
      )}

      {allChanges.length > 0 && (
        <>
          <div className="rounded-md border border-neutral-800 bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Change</TableHead>
                  <TableHead className="w-[140px]">When</TableHead>
                  <TableHead className="w-[140px]">User</TableHead>
                  <TableHead className="w-[200px]">Depot / Stream</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allChanges.map((c) => (
                  <TableRow key={c.change}>
                    <TableCell className="font-mono">
                      <Link
                        to={`/changes/${c.change}`}
                        className="text-theia-400 hover:underline"
                      >
                        @{c.change}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {relativeTime(c.time)}
                    </TableCell>
                    <TableCell className="font-mono text-muted-foreground">
                      {c.user ?? '—'}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate font-mono text-xs">
                      {c.streams && c.streams.length > 0 ? (
                        c.streams.map((s, i) => {
                          const p = s.replace(/^\/\//, '');
                          return (
                            <span key={s}>
                              {i > 0 && <span className="text-muted-foreground">, </span>}
                              <Link to={`/depot/${p}`} className="text-theia-400 hover:underline">
                                {p}
                              </Link>
                            </span>
                          );
                        })
                      ) : c.depots && c.depots.length > 0 ? (
                        <span className="text-muted-foreground">{c.depots.join(', ')}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[600px] truncate">
                      {firstLine(c.description) || (
                        <span className="text-muted-foreground italic">
                          (no description)
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage && <Spinner size="xs" />}
                Load older
                <IconChevronRight />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function firstLine(s: string): string {
  return s.split('\n')[0]?.trim() ?? '';
}

